/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import DOMPurify from 'isomorphic-dompurify'
import { EmailFrame } from '../TemplateRenderer'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import TitleIcon from '@mui/icons-material/Title'
import NotesIcon from '@mui/icons-material/Notes'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import SmartButtonIcon from '@mui/icons-material/SmartButton'
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule'
import HeightIcon from '@mui/icons-material/Height'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import CodeIcon from '@mui/icons-material/Code'
import {
    ACCENT,
    BRAND,
    EMAIL_CONTENT,
    FONT,
    ICON,
    RADIUS,
    SHADOW,
    STATUS,
    SURFACE,
    TEXT,
} from '../../theme/tokens'
import { BLOCK_TYPES, PALETTE_ORDER, makeBlock, newBlockId } from './blocks'
import { compileDesign } from './mjml'
import Inspector from './Inspector'

const PALETTE_ICONS = {
    heading: <TitleIcon />,
    paragraph: <NotesIcon />,
    image: <ImageOutlinedIcon />,
    button: <SmartButtonIcon />,
    divider: <HorizontalRuleIcon />,
    spacer: <HeightIcon />,
    columns: <ViewColumnIcon />,
    rawHtml: <CodeIcon />,
}

// The inline tags a block's text may carry (the email sends them as typed):
// shown as they will look, not as code. No `style` attribute: a template's
// text can come from an uploaded file, and a style could lay an element over
// the whole app (the previews use sandboxed frames for the same reason);
// colour comes from <font color>, links from href (sanitised, not clickable).
const INLINE = {
    ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 'small', 'br', 'a', 'span', 'font'],
    ALLOWED_ATTR: ['href', 'color'],
}
/** Props that show a block's text with its inline formatting (sanitised); links are not clickable on the canvas. */
const inlineHtml = (text) => ({
    dangerouslySetInnerHTML: { __html: DOMPurify.sanitize(String(text ?? ''), INLINE) },
})
const NO_LINKS = { '& a': { pointerEvents: 'none' } }

/** Lightweight visual approximation of a block inside the canvas. */
const BlockPreview = ({ block }) => {
    const p = block.props
    switch (block.type) {
        case 'heading': {
            const size = { h1: 24, h2: 19, h3: 16 }[p.level] ?? 24
            return (
                <Typography
                    sx={{
                        fontSize: size,
                        fontWeight: 700,
                        color: p.color,
                        textAlign: p.align,
                        wordBreak: 'break-word',
                        ...NO_LINKS,
                    }}
                    {...inlineHtml(p.text)}
                />
            )
        }
        case 'paragraph':
            return (
                <Typography
                    sx={{
                        fontSize: p.fontSize,
                        color: p.color,
                        textAlign: p.align,
                        lineHeight: p.lineHeight,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        ...NO_LINKS,
                    }}
                    {...inlineHtml(p.text)}
                />
            )
        case 'image':
            return p.src ? (
                <Box sx={{ textAlign: p.align }}>
                    <Box
                        component="img"
                        src={p.src}
                        alt={p.alt}
                        sx={{ maxWidth: '100%', width: p.width }}
                    />
                </Box>
            ) : (
                <Box
                    sx={{
                        height: 90,
                        border: `1px dashed ${EMAIL_CONTENT.hintBorder}`,
                        borderRadius: `${RADIUS.sm}px`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: EMAIL_CONTENT.hint,
                        typography: 'caption',
                    }}
                >
                    Image — set a URL in the panel →
                </Box>
            )
        case 'button':
            return (
                <Box sx={{ textAlign: p.align }}>
                    <Box
                        component="span"
                        sx={{
                            display: 'inline-block',
                            background: p.background,
                            color: p.color,
                            borderRadius: `${p.borderRadius}px`,
                            px: 3.5,
                            py: 1.2,
                            typography: 'body2',
                            fontWeight: 600,
                        }}
                        {...inlineHtml(p.text)}
                    />
                </Box>
            )
        case 'divider':
            return <Box sx={{ borderTop: `1px solid ${p.color}` }} />
        case 'spacer':
            return (
                <Box
                    sx={{
                        height: p.height,
                        background: `repeating-linear-gradient(45deg, transparent, transparent 6px, ${EMAIL_CONTENT.stripe} 6px, ${EMAIL_CONTENT.stripe} 12px)`,
                    }}
                />
            )
        case 'columns':
            return (
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {[p.leftText, p.rightText].map((t, i) => (
                        <Typography
                            key={i}
                            sx={{
                                flex: 1,
                                fontSize: p.fontSize,
                                color: p.color,
                                wordBreak: 'break-word',
                                ...NO_LINKS,
                            }}
                            {...inlineHtml(t)}
                        />
                    ))}
                </Box>
            )
        case 'rawHtml':
            // The email itself, as it will look (a generated or uploaded email is
            // one of these); its source is edited in the inspector. The frame is
            // not clickable, so a click selects the block.
            return p.html.trim() ? (
                <Box
                    sx={{
                        pointerEvents: 'none',
                        maxHeight: 560,
                        overflow: 'hidden',
                        borderRadius: `${RADIUS.sm}px`,
                    }}
                >
                    <EmailFrame
                        html={p.html}
                        minWidth={0}
                        startHeight={240}
                        title="HTML block preview"
                    />
                </Box>
            ) : (
                <Box
                    sx={{
                        typography: 'caption',
                        fontFamily: FONT.mono,
                        color: EMAIL_CONTENT.hint,
                        background: EMAIL_CONTENT.codeBg,
                        borderRadius: `${RADIUS.sm}px`,
                        p: 1,
                    }}
                >
                    Empty HTML block: add its HTML in the panel on the right.
                </Box>
            )
        default:
            return null
    }
}
BlockPreview.propTypes = { block: PropTypes.object.isRequired }

/**
 * In-house email builder. Controlled component:
 *   <EmailBuilder value={design} onChange={setDesign} />
 * Compile the final HTML at submit time with compileDesign(design).
 */
const EmailBuilder = ({ value, onChange }) => {
    const [selectedId, setSelectedId] = useState(null)
    const [previewMode, setPreviewMode] = useState(false)

    const design = value
    const blocks = design.blocks ?? []
    const selected = blocks.find((b) => b.id === selectedId) ?? null

    const patch = (next) => onChange({ ...design, ...next })
    const setBlocks = (nextBlocks) => patch({ blocks: nextBlocks })

    const addBlock = (type) => {
        const block = makeBlock(type)
        const idx = selected
            ? blocks.findIndex((b) => b.id === selected.id) + 1
            : blocks.length
        const next = [...blocks]
        next.splice(idx, 0, block)
        setBlocks(next)
        setSelectedId(block.id)
    }

    const move = (id, dir) => {
        const idx = blocks.findIndex((b) => b.id === id)
        const swap = idx + dir
        if (idx < 0 || swap < 0 || swap >= blocks.length) return
        const next = [...blocks]
        ;[next[idx], next[swap]] = [next[swap], next[idx]]
        setBlocks(next)
    }

    const duplicate = (id) => {
        const idx = blocks.findIndex((b) => b.id === id)
        if (idx < 0) return
        const copy = {
            ...blocks[idx],
            id: newBlockId(),
            props: { ...blocks[idx].props },
        }
        const next = [...blocks]
        next.splice(idx + 1, 0, copy)
        setBlocks(next)
        setSelectedId(copy.id)
    }

    const remove = (id) => {
        setBlocks(blocks.filter((b) => b.id !== id))
        if (selectedId === id) setSelectedId(null)
    }

    const [preview, setPreview] = useState(null)
    useEffect(() => {
        if (!previewMode) {
            setPreview(null)
            return undefined
        }
        let cancelled = false
        compileDesign(design).then((result) => {
            if (!cancelled) setPreview(result)
        })
        return () => {
            cancelled = true
        }
    }, [previewMode, design])

    return (
        <Box
            sx={{
                display: 'flex',
                // On a phone the panel goes under the canvas: side by side,
                // its fixed 280px pushed the page wider than the screen.
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                alignItems: 'stretch',
                minHeight: 480,
            }}
        >
            {/* Canvas */}
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    background: design.body.backgroundColor,
                    borderRadius: `${RADIUS.md}px`,
                    border: `1px solid ${SURFACE.border}`,
                    p: 3,
                    overflowY: 'auto',
                    maxHeight: 640,
                }}
                onClick={() => setSelectedId(null)}
            >
                {previewMode ? (
                    <Box
                        component="iframe"
                        title="Email preview"
                        srcDoc={preview?.html ?? ''}
                        sx={{
                            width: '100%',
                            height: 580,
                            border: 'none',
                            background: SURFACE.emailCanvas,
                            borderRadius: `${RADIUS.sm}px`,
                        }}
                    />
                ) : (
                    <Box
                        sx={{
                            maxWidth: design.body.contentWidth,
                            mx: 'auto',
                            background: design.body.contentBackground,
                            borderRadius: `${RADIUS.sm}px`,
                            overflow: 'hidden',
                            boxShadow: SHADOW.card,
                        }}
                    >
                        {blocks.length === 0 && (
                            <Typography
                                sx={{
                                    p: 6,
                                    textAlign: 'center',
                                    color: EMAIL_CONTENT.hint,
                                    typography: 'body2',
                                }}
                            >
                                Empty email — add blocks from the panel on the
                                right.
                            </Typography>
                        )}
                        {blocks.map((block) => {
                            const isSel = block.id === selectedId
                            return (
                                <Box
                                    key={block.id}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setSelectedId(block.id)
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={`${
                                        BLOCK_TYPES[block.type].label
                                    } block`}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.stopPropagation()
                                            setSelectedId(block.id)
                                        }
                                    }}
                                    sx={{
                                        position: 'relative',
                                        px: 3,
                                        py: 1.25,
                                        cursor: 'pointer',
                                        outline: isSel
                                            ? `2px solid ${BRAND.orange}`
                                            : '2px solid transparent',
                                        outlineOffset: -2,
                                        '&:hover': {
                                            outline: isSel
                                                ? `2px solid ${BRAND.orange}`
                                                : `2px dashed ${ACCENT.border55}`,
                                        },
                                    }}
                                >
                                    <BlockPreview block={block} />
                                    {isSel && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 2,
                                                right: 2,
                                                display: 'flex',
                                                background: SURFACE.cardRaised,
                                                borderRadius: `${RADIUS.sm}px`,
                                                border: `1px solid ${SURFACE.border}`,
                                                zIndex: 1,
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <Tooltip title="Move up">
                                                <IconButton
                                                    size="small"
                                                    aria-label="Move block up"
                                                    onClick={() =>
                                                        move(block.id, -1)
                                                    }
                                                >
                                                    <ArrowUpwardIcon
                                                        sx={{
                                                            fontSize: ICON.sm,
                                                            color: TEXT.secondary,
                                                        }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Move down">
                                                <IconButton
                                                    size="small"
                                                    aria-label="Move block down"
                                                    onClick={() =>
                                                        move(block.id, 1)
                                                    }
                                                >
                                                    <ArrowDownwardIcon
                                                        sx={{
                                                            fontSize: ICON.sm,
                                                            color: TEXT.secondary,
                                                        }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Duplicate">
                                                <IconButton
                                                    size="small"
                                                    aria-label="Duplicate block"
                                                    onClick={() =>
                                                        duplicate(block.id)
                                                    }
                                                >
                                                    <ContentCopyIcon
                                                        sx={{
                                                            fontSize: ICON.sm,
                                                            color: TEXT.secondary,
                                                        }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    aria-label="Delete block"
                                                    onClick={() =>
                                                        remove(block.id)
                                                    }
                                                >
                                                    <DeleteOutlineIcon
                                                        sx={{
                                                            fontSize: ICON.sm,
                                                            color: STATUS.error
                                                                .fg,
                                                        }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    )}
                                </Box>
                            )
                        })}
                    </Box>
                )}
            </Box>

            {/* Right panel */}
            <Box
                sx={{
                    width: { xs: '100%', md: 280 },
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <ButtonBase
                    onClick={() => setPreviewMode((m) => !m)}
                    aria-pressed={previewMode}
                    sx={{
                        alignSelf: 'flex-start',
                        display: 'inline-flex',
                        gap: 0.75,
                        alignItems: 'center',
                        px: 1.5,
                        py: 0.6,
                        borderRadius: `${RADIUS.pill}px`,
                        typography: 'caption',
                        fontWeight: 600,
                        color: previewMode ? ACCENT.text : TEXT.secondary,
                        background: previewMode ? ACCENT.bg12 : SURFACE.inputBg,
                        border: `1px solid ${
                            previewMode ? ACCENT.border30 : SURFACE.border
                        }`,
                    }}
                >
                    {previewMode ? (
                        <EditOutlinedIcon sx={{ fontSize: ICON.sm }} />
                    ) : (
                        <VisibilityOutlinedIcon sx={{ fontSize: ICON.sm }} />
                    )}
                    {previewMode ? 'Back to editing' : 'Preview compiled email'}
                </ButtonBase>

                {!previewMode && (
                    <>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns:
                                    'repeat(4, minmax(0, 1fr))',
                                gap: '6px',
                            }}
                        >
                            {PALETTE_ORDER.map((type) => (
                                <Tooltip
                                    key={type}
                                    title={`Add ${BLOCK_TYPES[type].label}`}
                                >
                                    <ButtonBase
                                        onClick={() => addBlock(type)}
                                        aria-label={`Add ${BLOCK_TYPES[type].label} block`}
                                        sx={{
                                            flexDirection: 'column',
                                            gap: 0.5,
                                            py: 1,
                                            borderRadius: `${RADIUS.sm}px`,
                                            border: `1px solid ${SURFACE.border}`,
                                            background: SURFACE.inputBg,
                                            color: TEXT.secondary,
                                            '& svg': { fontSize: ICON.md },
                                            '&:hover': {
                                                borderColor: ACCENT.border30,
                                                color: ACCENT.text,
                                            },
                                        }}
                                    >
                                        {PALETTE_ICONS[type]}
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 500,
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {BLOCK_TYPES[type].label}
                                        </Typography>
                                    </ButtonBase>
                                </Tooltip>
                            ))}
                        </Box>

                        <Box
                            sx={{
                                background: SURFACE.card,
                                border: `1px solid ${SURFACE.border}`,
                                borderRadius: `${RADIUS.md}px`,
                                p: 2,
                                flex: 1,
                                overflowY: 'auto',
                                maxHeight: 520,
                            }}
                        >
                            <Typography
                                variant="overline"
                                sx={{
                                    display: 'block',
                                    color: ACCENT.text,
                                    mb: 1.5,
                                }}
                            >
                                {selected
                                    ? `${
                                          BLOCK_TYPES[selected.type].label
                                      } settings`
                                    : 'Email settings'}
                            </Typography>
                            <Inspector
                                block={selected}
                                body={design.body}
                                onChangeProps={(propsPatch) =>
                                    setBlocks(
                                        blocks.map((b) =>
                                            b.id === selected.id
                                                ? {
                                                      ...b,
                                                      props: {
                                                          ...b.props,
                                                          ...propsPatch,
                                                      },
                                                  }
                                                : b
                                        )
                                    )
                                }
                                onChangeBody={(bodyPatch) =>
                                    patch({
                                        body: { ...design.body, ...bodyPatch },
                                    })
                                }
                            />
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    )
}

EmailBuilder.propTypes = {
    value: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
}

export default EmailBuilder
